import {
  MenuGroup,
  MenuItem,
  MenuList,
  tokens
} from "@fluentui/react-components";
import {
  AddSquare20Regular,
  Delete20Regular,
  TableDeleteColumn20Regular,
  TableDeleteRow20Regular,
  TableInsertColumn20Regular,
  TableInsertRow20Regular
} from "@fluentui/react-icons";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $deleteTableColumn__EXPERIMENTAL,
  $deleteTableRow__EXPERIMENTAL,
  $getTableNodeFromLexicalNodeOrThrow,
  $insertTableColumn__EXPERIMENTAL,
  $insertTableRow__EXPERIMENTAL,
  $isTableCellNode,
  $isTableSelection,
  TableCellNode
} from "@lexical/table";
import { $getNodeByKey, $getSelection, LexicalEditor } from "lexical";
import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  $isCustomTableCellNode,
  CustomTableCellNode
} from "../nodes/CustomTableCellNode";
import { ColorPicker } from "./ColorPicker";

const DEFAULT_BACKGROUND_COLOR = "#FFFFFF";

type TableActionMenuProps = {
  onClose: () => void;
  tableCellNode: TableCellNode | CustomTableCellNode;
  cellEditor: LexicalEditor;
};

function TableActionMenu({
  onClose,
  tableCellNode
}: TableActionMenuProps): React.JSX.Element {
  const [editor] = useLexicalComposerContext();
  const [backgroundColor, setBackgroundColor] = useState(
    DEFAULT_BACKGROUND_COLOR
  );

  useEffect(() => {
    editor.getEditorState().read(() => {
      const color =
        tableCellNode.getBackgroundColor() || DEFAULT_BACKGROUND_COLOR;
      setBackgroundColor(color.toUpperCase());
    });
  }, [editor, tableCellNode]);

  const insertTableRowAbove = useCallback(() => {
    editor.update(() => {
      $insertTableRow__EXPERIMENTAL(false);
      onClose();
    });
  }, [editor, onClose]);

  const insertTableRowBelow = useCallback(() => {
    editor.update(() => {
      $insertTableRow__EXPERIMENTAL(true);
      onClose();
    });
  }, [editor, onClose]);

  const insertTableColumnBefore = useCallback(() => {
    editor.update(() => {
      $insertTableColumn__EXPERIMENTAL(false);
      onClose();
    });
  }, [editor, onClose]);

  const insertTableColumnAfter = useCallback(() => {
    editor.update(() => {
      $insertTableColumn__EXPERIMENTAL(true);
      onClose();
    });
  }, [editor, onClose]);

  const deleteTableRow = useCallback(() => {
    editor.update(() => {
      $deleteTableRow__EXPERIMENTAL();
      onClose();
    });
  }, [editor, onClose]);

  const deleteTableColumn = useCallback(() => {
    editor.update(() => {
      $deleteTableColumn__EXPERIMENTAL();
      onClose();
    });
  }, [editor, onClose]);

  const deleteTable = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
      tableNode.remove();
      onClose();
    });
  }, [editor, tableCellNode, onClose]);

  const handleBackgroundColorChange = useCallback(
    (color: string) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isTableSelection(selection)) {
          const nodes = selection.getNodes();
          for (const node of nodes) {
            if ($isTableCellNode(node) || $isCustomTableCellNode(node)) {
              const writableNode = node.getWritable();
              writableNode.setBackgroundColor(color);
            }
          }
        } else {
          const writableNode = tableCellNode.getWritable();
          writableNode.setBackgroundColor(color);
        }
      });
      setBackgroundColor(color);
    },
    [editor, tableCellNode]
  );

  return (
    <MenuList className="table-action-menu-list">
      <MenuGroup className="table-menu-group">
        <MenuItem className="table-menu-group-header">Rader</MenuItem>
        <MenuItem
          icon={<TableInsertRow20Regular />}
          onClick={insertTableRowAbove}
          className="table-menu-item"
        >
          Infoga rad ovanför
        </MenuItem>
        <MenuItem
          icon={<AddSquare20Regular />}
          onClick={insertTableRowBelow}
          className="table-menu-item"
        >
          Infoga rad nedanför
        </MenuItem>
        <MenuItem
          icon={
            <TableDeleteRow20Regular
              style={{ color: tokens.colorPaletteRedForeground1 }}
            />
          }
          onClick={deleteTableRow}
          className="table-menu-item table-menu-item-danger"
        >
          Ta bort rad
        </MenuItem>
      </MenuGroup>

      <MenuGroup className="table-menu-group">
        <MenuItem className="table-menu-group-header">Kolumner</MenuItem>
        <MenuItem
          icon={<TableInsertColumn20Regular />}
          onClick={insertTableColumnBefore}
          className="table-menu-item"
        >
          Infoga kolumn till vänster
        </MenuItem>
        <MenuItem
          icon={<AddSquare20Regular />}
          onClick={insertTableColumnAfter}
          className="table-menu-item"
        >
          Infoga kolumn till höger
        </MenuItem>
        <MenuItem
          icon={
            <TableDeleteColumn20Regular
              style={{ color: tokens.colorPaletteRedForeground1 }}
            />
          }
          onClick={deleteTableColumn}
          className="table-menu-item table-menu-item-danger"
        >
          Ta bort kolumn
        </MenuItem>
      </MenuGroup>

      <MenuGroup className="table-menu-group">
        <MenuItem className="table-menu-group-header">Bakgrundsfärg</MenuItem>
        <div className="table-color-picker-inline">
          <ColorPicker
            selectedColor={backgroundColor}
            onColorChange={handleBackgroundColorChange}
          />
        </div>
      </MenuGroup>

      <MenuItem
        icon={
          <Delete20Regular
            style={{ color: tokens.colorPaletteRedForeground1 }}
          />
        }
        onClick={deleteTable}
        className="table-menu-item table-menu-clear-formatting"
      >
        Ta bort tabell
      </MenuItem>
    </MenuList>
  );
}

const MENU_WIDTH = 220;
const MENU_HEIGHT = 400;

function TableCellActionMenuContainer(): React.JSX.Element | null {
  const [editor] = useLexicalComposerContext();
  const [tableCellNode, setTableCellNode] = useState<
    TableCellNode | CustomTableCellNode | null
  >(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const longPressTimerRef = React.useRef<number | null>(null);
  const touchStartPosRef = React.useRef<{ x: number; y: number } | null>(null);

  const openMenuAtPosition = useCallback(
    (cellElement: Element, clientX: number, clientY: number) => {
      const key = cellElement.getAttribute(
        "data-temporary-table-cell-lexical-key"
      );
      if (!key) {
        return;
      }

      editor.update(() => {
        const node = $getNodeByKey(key);
        if (
          !node ||
          (!$isTableCellNode(node) && !$isCustomTableCellNode(node))
        ) {
          return;
        }

        // Calculate menu position with viewport checks
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const padding = 20; // Padding from viewport edges
        let x = clientX;
        let y = clientY;

        // Check if menu would overflow right edge
        if (x + MENU_WIDTH > viewportWidth - padding) {
          x = Math.max(padding, viewportWidth - MENU_WIDTH - padding);
        }

        // Check if menu would overflow bottom edge
        if (y + MENU_HEIGHT > viewportHeight - padding) {
          y = Math.max(padding, viewportHeight - MENU_HEIGHT - padding);
        }

        // Ensure menu doesn't go off left edge
        if (x < padding) {
          x = padding;
        }

        // Ensure menu doesn't go off top edge
        if (y < padding) {
          y = padding;
        }

        setMenuPosition({ x, y });
        setTableCellNode(node as TableCellNode | CustomTableCellNode);
        setIsMenuOpen(true);
      });
    },
    [editor]
  );

  const handleContextMenu = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const cellElement = target.closest("td, th");
      if (!cellElement) {
        return;
      }

      event.preventDefault();
      openMenuAtPosition(cellElement, event.clientX, event.clientY);
    },
    [openMenuAtPosition]
  );

  const handleTouchStart = useCallback(
    (event: TouchEvent) => {
      const target = event.target as HTMLElement;
      const cellElement = target.closest("td, th");
      if (!cellElement) {
        return;
      }

      const touch = event.touches[0];
      touchStartPosRef.current = { x: touch.clientX, y: touch.clientY };

      // Start long press timer (500ms)
      longPressTimerRef.current = window.setTimeout(() => {
        if (touchStartPosRef.current) {
          // Trigger haptic feedback if available
          if (navigator.vibrate) {
            navigator.vibrate(50);
          }
          event.preventDefault();
          openMenuAtPosition(
            cellElement,
            touchStartPosRef.current.x,
            touchStartPosRef.current.y
          );
        }
      }, 500);
    },
    [openMenuAtPosition]
  );

  const handleTouchMove = useCallback((event: TouchEvent) => {
    if (longPressTimerRef.current && touchStartPosRef.current) {
      const touch = event.touches[0];
      const moveThreshold = 10; // pixels
      const dx = Math.abs(touch.clientX - touchStartPosRef.current.x);
      const dy = Math.abs(touch.clientY - touchStartPosRef.current.y);

      // Cancel long press if user moves finger too much
      if (dx > moveThreshold || dy > moveThreshold) {
        window.clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
        touchStartPosRef.current = null;
      }
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    // Cancel long press if touch ends before timer
    if (longPressTimerRef.current) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
    touchStartPosRef.current = null;
  }, []);

  useEffect(() => {
    return editor.registerRootListener((rootElement, prevRootElement) => {
      if (prevRootElement) {
        prevRootElement.removeEventListener("contextmenu", handleContextMenu);
        prevRootElement.removeEventListener("touchstart", handleTouchStart);
        prevRootElement.removeEventListener("touchmove", handleTouchMove);
        prevRootElement.removeEventListener("touchend", handleTouchEnd);
        prevRootElement.removeEventListener("touchcancel", handleTouchEnd);
      }
      if (rootElement) {
        rootElement.addEventListener("contextmenu", handleContextMenu);
        rootElement.addEventListener("touchstart", handleTouchStart, {
          passive: false
        });
        rootElement.addEventListener("touchmove", handleTouchMove, {
          passive: true
        });
        rootElement.addEventListener("touchend", handleTouchEnd, {
          passive: true
        });
        rootElement.addEventListener("touchcancel", handleTouchEnd, {
          passive: true
        });
      }
    });
  }, [
    editor,
    handleContextMenu,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd
  ]);

  const handleClose = useCallback(() => {
    setIsMenuOpen(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest(".table-action-menu-container")) {
        setIsMenuOpen(false);
      }
    };

    const handleScroll = (event: Event) => {
      if ((event.target as HTMLElement).closest(".table-action-menu-container")) {
        return;
      }
      setIsMenuOpen(false);
    };

    if (isMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll, true);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        window.removeEventListener("scroll", handleScroll, true);
      };
    }
  }, [isMenuOpen]);

  if (!tableCellNode || !isMenuOpen) {
    return null;
  }

  return createPortal(
    <div
      className="table-action-menu-container"
      style={{
        position: "fixed",
        left: `${menuPosition.x}px`,
        top: `${menuPosition.y}px`,
        zIndex: 1000
      }}
    >
      <TableActionMenu
        onClose={handleClose}
        tableCellNode={tableCellNode}
        cellEditor={editor}
      />
    </div>,
    document.body
  );
}

export default function TableActionMenuPlugin(): React.JSX.Element | null {
  return <TableCellActionMenuContainer />;
}
